import {
  Opcode,
  SenseTarget,
  DIR_HERE,
  DIR_RANDOM,
  type Instruction,
  type Operand,
  type Program,
} from './types';

export class ParseError extends Error {
  constructor(
    public line: number,
    message: string,
  ) {
    super(`Line ${line}: ${message}`);
  }
}

export type ParseResult =
  | { ok: true; program: Program; errors: [] }
  | { ok: false; program: null; errors: ParseError[] };

// ─── Shared constants ───────────────────────────────────────────────

const ABSDIRS: Record<string, number> = {
  HERE: DIR_HERE,
  N: 1, E: 2, S: 3, W: 4,
  NORTH: 1, EAST: 2, SOUTH: 3, WEST: 4,
};

const SENSE_TARGETS: Record<string, SenseTarget> = {
  FOOD: SenseTarget.FOOD,
  WALL: SenseTarget.WALL,
  NEST: SenseTarget.NEST,
  ANT: SenseTarget.ANT,
  EMPTY: SenseTarget.EMPTY,
};

const CHANNEL_NAMES: Record<string, number> = {
  CH_RED: 0, CH_BLUE: 1, CH_GREEN: 2, CH_YELLOW: 3,
};

// ─── Legacy parser ──────────────────────────────────────────────────

const OPCODES: Record<string, Opcode> = {
  SENSE: Opcode.SENSE, SMELL: Opcode.SMELL, SNIFF: Opcode.SNIFF, PROBE: Opcode.PROBE,
  CARRYING: Opcode.CARRYING, SET: Opcode.SET, ADD: Opcode.ADD,
  SUB: Opcode.SUB, MOD: Opcode.MOD, MUL: Opcode.MUL,
  DIV: Opcode.DIV, AND: Opcode.AND, OR: Opcode.OR,
  XOR: Opcode.XOR, LSHIFT: Opcode.LSHIFT, RSHIFT: Opcode.RSHIFT,
  RANDOM: Opcode.RANDOM,
  MARK: Opcode.MARK, JMP: Opcode.JMP, CALL: Opcode.CALL,
  JEQ: Opcode.JEQ, JNE: Opcode.JNE, JGT: Opcode.JGT, JLT: Opcode.JLT,
  MOVE: Opcode.MOVE, PICKUP: Opcode.PICKUP, DROP: Opcode.DROP,
  ID: Opcode.ID, NOP: Opcode.NOP, TAG: Opcode.TAG,
};

function parseRegister(token: string): number | null {
  const m = /^[rR]([0-7])$/.exec(token);
  return m ? parseInt(m[1]) : null;
}

function parseOperand(token: string): Operand | null {
  const reg = parseRegister(token);
  if (reg !== null) return { type: 'reg', val: reg };
  const upper = token.toUpperCase();
  const ch = CHANNEL_NAMES[upper];
  if (ch !== undefined) return { type: 'lit', val: ch };
  const dir = ABSDIRS[upper];
  if (dir !== undefined) return { type: 'lit', val: dir };
  if (upper === 'RANDOM') return { type: 'lit', val: DIR_RANDOM };
  const st = SENSE_TARGETS[upper];
  if (st !== undefined) return { type: 'lit', val: st };
  const num = /^-?0x[\da-f]+$/i.test(token) ? parseInt(token, 16) : parseInt(token, 10);
  if (!isNaN(num)) return { type: 'lit', val: num };
  return null;
}

function parseDirOperand(token: string, lineNum: number): Operand {
  const upper = token.toUpperCase();
  if (upper === 'RANDOM') return { type: 'lit', val: DIR_RANDOM };
  const dir = ABSDIRS[upper];
  if (dir !== undefined) return { type: 'lit', val: dir };
  const op = parseOperand(token);
  if (op) return op;
  throw new ParseError(lineNum, `Invalid direction: "${token}" (use N, E, S, W, RANDOM, or register)`);
}

export function parse(source: string): Program {
  const lines = source.split('\n');
  const labels = new Map<string, number>();
  const aliases = new Map<string, string>();
  const consts = new Map<string, string>();
  const tagNames = new Map<number, string>();
  const rawInstructions: { lineNum: number; tokens: string[] }[] = [];

  // Substitution: replace aliases and consts in tokens
  function subst(token: string): string {
    return aliases.get(token) ?? consts.get(token) ?? token;
  }

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    const commentIdx = line.indexOf(';');
    if (commentIdx >= 0) line = line.substring(0, commentIdx);
    line = line.trim();
    if (!line) continue;

    const firstToken = line.split(/\s+/)[0];

    if (firstToken.startsWith('.') && firstToken !== '.alias' && firstToken !== '.const' && firstToken !== '.tag') {
      throw new ParseError(i + 1, `Unknown directive: "${firstToken}"`);
    }

    // .alias directive: .alias <name> <register>
    if (firstToken === '.alias') {
      const parts = line.split(/\s+/);
      if (parts.length !== 3) throw new ParseError(i + 1, '.alias requires: .alias <name> <register>');
      const name = parts[1];
      const reg = parseRegister(parts[2]);
      if (reg === null) throw new ParseError(i + 1, `Expected register (r0-r7), got: "${parts[2]}"`);
      if (aliases.has(name) || consts.has(name)) throw new ParseError(i + 1, `"${name}" already defined`);
      aliases.set(name, parts[2]);
      continue;
    }

    // .const directive: .const <NAME> <value>
    if (firstToken === '.const') {
      const parts = line.split(/\s+/);
      if (parts.length !== 3) throw new ParseError(i + 1, '.const requires: .const <NAME> <value>');
      const name = parts[1];
      if (aliases.has(name) || consts.has(name)) throw new ParseError(i + 1, `"${name}" already defined`);
      consts.set(name, parts[2]);
      continue;
    }

    // .tag directive: .tag <n> <name>
    if (firstToken === '.tag') {
      const parts = line.split(/\s+/);
      if (parts.length < 3) throw new ParseError(i + 1, '.tag requires: .tag <number> <name>');
      const tagNum = /^0x[\da-f]+$/i.test(parts[1]) ? parseInt(parts[1], 16) : parseInt(parts[1], 10);
      if (isNaN(tagNum) || tagNum < 0 || tagNum > 7) throw new ParseError(i + 1, `.tag number must be 0-7, got: "${parts[1]}"`);
      const tagName = parts[2];
      tagNames.set(tagNum, parts.slice(2).join(' '));
      if (!aliases.has(tagName) && !consts.has(tagName)) {
        consts.set(tagName, String(tagNum));
      }
      continue;
    }

    if (line.endsWith(':')) {
      const label = line.slice(0, -1).trim();
      if (!label || /\s/.test(label)) {
        throw new ParseError(i + 1, `Invalid label: "${label}"`);
      }
      if (labels.has(label)) {
        throw new ParseError(i + 1, `Duplicate label: "${label}"`);
      }
      labels.set(label, rawInstructions.length);
      continue;
    }

    const tokens = line.split(/\s+/);
    // Substitute aliases/consts in args (not the opcode)
    for (let j = 1; j < tokens.length; j++) {
      tokens[j] = subst(tokens[j]);
    }
    rawInstructions.push({ lineNum: i + 1, tokens });
  }

  const instructions: Instruction[] = rawInstructions.map(({ lineNum, tokens }) => {
    const opName = tokens[0].toUpperCase();
    const op = OPCODES[opName];
    if (op === undefined) {
      throw new ParseError(lineNum, `Unknown instruction: "${tokens[0]}"`);
    }

    const args = tokens.slice(1);

    switch (op) {
      case Opcode.SENSE: {
        if (args.length < 1 || args.length > 2) throw new ParseError(lineNum, 'SENSE requires 1-2 args: <target> [dest_reg]');
        const namedTarget = SENSE_TARGETS[args[0].toUpperCase()];
        let targetOp: Operand;
        if (namedTarget !== undefined) {
          targetOp = { type: 'lit' as const, val: namedTarget };
        } else {
          const op0 = parseOperand(args[0]);
          if (!op0) throw new ParseError(lineNum, `Invalid sense target: "${args[0]}"`);
          targetOp = op0;
        }
        let destReg = 0;
        if (args.length === 2) {
          const r = parseRegister(args[1]);
          if (r === null) throw new ParseError(lineNum, `Expected register for destination, got: "${args[1]}"`);
          destReg = r;
        }
        return { op, operands: [targetOp, { type: 'lit' as const, val: destReg }] };
      }

      case Opcode.SMELL: {
        if (args.length < 1 || args.length > 2) throw new ParseError(lineNum, 'SMELL requires 1-2 args: <channel> [dest_reg]');
        const ch = parseOperand(args[0]);
        if (!ch) throw new ParseError(lineNum, `Invalid channel: "${args[0]}"`);
        let destReg = 0;
        if (args.length === 2) {
          const r = parseRegister(args[1]);
          if (r === null) throw new ParseError(lineNum, `Expected register for destination, got: "${args[1]}"`);
          destReg = r;
        }
        return { op, operands: [ch, { type: 'lit' as const, val: destReg }] };
      }

      case Opcode.SNIFF: {
        if (args.length < 2 || args.length > 3) throw new ParseError(lineNum, 'SNIFF requires 2-3 args: <channel> <dir> [dest_reg]');
        const ch = parseOperand(args[0]);
        if (!ch) throw new ParseError(lineNum, `Invalid channel: "${args[0]}"`);
        const dir = parseDirOperand(args[1], lineNum);
        let destReg = 0;
        if (args.length === 3) {
          const r = parseRegister(args[2]);
          if (r === null) throw new ParseError(lineNum, `Expected register for destination, got: "${args[2]}"`);
          destReg = r;
        }
        return { op, operands: [ch, dir, { type: 'lit' as const, val: destReg }] };
      }

      case Opcode.PROBE: {
        if (args.length < 1 || args.length > 2) throw new ParseError(lineNum, 'PROBE requires 1-2 args: <dir> [dest_reg]');
        const dir = parseDirOperand(args[0], lineNum);
        let destReg = 0;
        if (args.length === 2) {
          const r = parseRegister(args[1]);
          if (r === null) throw new ParseError(lineNum, `Expected register for destination, got: "${args[1]}"`);
          destReg = r;
        }
        return { op, operands: [dir, { type: 'lit' as const, val: destReg }] };
      }

      case Opcode.CARRYING: {
        if (args.length > 1) throw new ParseError(lineNum, 'CARRYING takes 0-1 args: [dest_reg]');
        let destReg = 0;
        if (args.length === 1) {
          const r = parseRegister(args[0]);
          if (r === null) throw new ParseError(lineNum, `Expected register for destination, got: "${args[0]}"`);
          destReg = r;
        }
        return { op, operands: [{ type: 'lit' as const, val: destReg }] };
      }

      case Opcode.ID: {
        if (args.length > 1) throw new ParseError(lineNum, 'ID takes 0-1 args: [dest_reg]');
        let destReg = 0;
        if (args.length === 1) {
          const r = parseRegister(args[0]);
          if (r === null) throw new ParseError(lineNum, `Expected register for destination, got: "${args[0]}"`);
          destReg = r;
        }
        return { op, operands: [{ type: 'lit' as const, val: destReg }] };
      }

      case Opcode.PICKUP:
      case Opcode.DROP:
      case Opcode.NOP: {
        if (args.length !== 0) throw new ParseError(lineNum, `${opName} takes no arguments`);
        return { op, operands: [] };
      }

      case Opcode.MOVE: {
        if (args.length !== 1) throw new ParseError(lineNum, 'MOVE requires 1 arg: N, E, S, W, or register');
        const dir = parseDirOperand(args[0], lineNum);
        return { op, operands: [dir] };
      }

      case Opcode.SET:
      case Opcode.ADD:
      case Opcode.SUB:
      case Opcode.MOD:
      case Opcode.MUL:
      case Opcode.DIV:
      case Opcode.AND:
      case Opcode.OR:
      case Opcode.XOR:
      case Opcode.LSHIFT:
      case Opcode.RSHIFT: {
        if (args.length !== 2) throw new ParseError(lineNum, `${opName} requires 2 args: <reg> <val>`);
        const reg = parseRegister(args[0]);
        if (reg === null) throw new ParseError(lineNum, `Expected register, got: "${args[0]}"`);
        const val = parseOperand(args[1]);
        if (!val) throw new ParseError(lineNum, `Invalid value: "${args[1]}"`);
        return { op, operands: [{ type: 'lit' as const, val: reg }, val] };
      }

      case Opcode.RANDOM: {
        if (args.length !== 2) throw new ParseError(lineNum, 'RANDOM requires 2 args: <reg> <max>');
        const reg = parseRegister(args[0]);
        if (reg === null) throw new ParseError(lineNum, `Expected register, got: "${args[0]}"`);
        const max = parseOperand(args[1]);
        if (!max) throw new ParseError(lineNum, `Invalid max: "${args[1]}"`);
        return { op, operands: [{ type: 'lit' as const, val: reg }, max] };
      }

      case Opcode.MARK: {
        if (args.length !== 2) throw new ParseError(lineNum, 'MARK requires 2 args: <channel> <intensity>');
        const ch = parseOperand(args[0]);
        const intensity = parseOperand(args[1]);
        if (!ch) throw new ParseError(lineNum, `Invalid channel: "${args[0]}"`);
        if (!intensity) throw new ParseError(lineNum, `Invalid intensity: "${args[1]}"`);
        return { op, operands: [ch, intensity] };
      }

      case Opcode.JMP: {
        if (args.length !== 1) throw new ParseError(lineNum, 'JMP requires 1 arg: <label> or <register>');
        const reg = parseRegister(args[0]);
        if (reg !== null) return { op, operands: [{ type: 'reg' as const, val: reg }] };
        const addr = labels.get(args[0]);
        if (addr === undefined) throw new ParseError(lineNum, `Unknown label: "${args[0]}"`);
        return { op, operands: [{ type: 'lit' as const, val: addr }] };
      }

      case Opcode.CALL: {
        if (args.length !== 2) throw new ParseError(lineNum, 'CALL requires 2 args: <reg> <label>');
        const reg = parseRegister(args[0]);
        if (reg === null) throw new ParseError(lineNum, `Expected register, got: "${args[0]}"`);
        const addr = labels.get(args[1]);
        if (addr === undefined) throw new ParseError(lineNum, `Unknown label: "${args[1]}"`);
        return { op, operands: [{ type: 'lit' as const, val: reg }, { type: 'lit' as const, val: addr }] };
      }

      case Opcode.JEQ:
      case Opcode.JNE:
      case Opcode.JGT:
      case Opcode.JLT: {
        if (args.length !== 3) throw new ParseError(lineNum, `${opName} requires 3 args: <val> <val> <label>`);
        const a = parseOperand(args[0]);
        const b = parseOperand(args[1]);
        if (!a) throw new ParseError(lineNum, `Invalid operand: "${args[0]}"`);
        if (!b) throw new ParseError(lineNum, `Invalid operand: "${args[1]}"`);
        const addr = labels.get(args[2]);
        if (addr === undefined) throw new ParseError(lineNum, `Unknown label: "${args[2]}"`);
        return { op, operands: [a, b, { type: 'lit' as const, val: addr }] };
      }

      case Opcode.TAG: {
        if (args.length !== 1) throw new ParseError(lineNum, 'TAG requires 1 arg: <value> or <register>');
        const val = parseOperand(args[0]);
        if (!val) throw new ParseError(lineNum, `Invalid TAG value: "${args[0]}"`);
        return { op, operands: [val] };
      }

      default:
        throw new ParseError(lineNum, `Unhandled opcode: ${opName}`);
    }
  });

  if (instructions.length === 0) {
    throw new ParseError(0, 'Program is empty');
  }

  const sourceLines = rawInstructions.map(({ lineNum }) => lineNum);

  // If there's a "main" label and it's not at instruction 0, prepend JMP main
  const mainAddr = labels.get('main');
  if (mainAddr !== undefined && mainAddr !== 0) {
    const jmpInstr: Instruction = { op: Opcode.JMP, operands: [{ type: 'lit', val: mainAddr + 1 }] };
    for (const instr of instructions) {
      if (instr.op === Opcode.JMP && instr.operands[0].type === 'lit') {
        instr.operands[0].val++;
      } else if (instr.op === Opcode.CALL) {
        instr.operands[1].val++;
      } else if (instr.op === Opcode.JEQ || instr.op === Opcode.JNE || instr.op === Opcode.JGT || instr.op === Opcode.JLT) {
        instr.operands[2].val++;
      }
    }
    instructions.unshift(jmpInstr);
    sourceLines.unshift(0);
  }

  // Build reverse map: register index → first alias name
  const registerAliases = new Map<number, string>();
  for (const [name, regStr] of aliases) {
    const reg = parseRegister(regStr);
    if (reg !== null && !registerAliases.has(reg)) {
      registerAliases.set(reg, name);
    }
  }

  return {
    instructions,
    sourceLines,
    tagNames: tagNames.size > 0 ? tagNames : undefined,
    registerAliases: registerAliases.size > 0 ? registerAliases : undefined,
  };
}

export function tryParse(source: string): ParseResult {
  const errors: ParseError[] = [];
  try {
    const program = parse(source);
    return { ok: true, program, errors: [] as unknown as [] };
  } catch (e) {
    if (e instanceof ParseError) errors.push(e);
    else return { ok: false, program: null, errors: [new ParseError(0, 'Parse error')] };
  }

  // First error collected; try to find more by re-parsing lines individually.
  // The parser throws on first error, so we do a brute approach:
  // comment out the erroring line and re-parse to find subsequent errors.
  const lines = source.split('\n');
  const tried = new Set<number>();
  tried.add(errors[0].line);

  for (let attempt = 0; attempt < 50; attempt++) {
    const patched = lines.map((l, i) => tried.has(i + 1) ? '; [error]' : l).join('\n');
    try {
      parse(patched);
      break;
    } catch (e) {
      if (e instanceof ParseError && !tried.has(e.line)) {
        errors.push(e);
        tried.add(e.line);
      } else {
        break;
      }
    }
  }

  return { ok: false, program: null, errors };
}

